import { createFileRoute, redirect } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  ExternalLink,
  RotateCw,
  Trash2,
  CheckCircle2,
  AlertCircle,
  Copy,
} from "lucide-react";
import { adminCheckRole } from "@/lib/admin.functions";

export const Route = createFileRoute("/_authenticated/admin/share-debug")({
  head: () => ({
    meta: [
      { title: "Share preview debugger — Admin" },
      { name: "robots", content: "noindex" },
    ],
  }),
  beforeLoad: async () => {
    try {
      const { isAdmin } = await adminCheckRole();
      if (!isAdmin) throw redirect({ to: "/dashboard" });
    } catch (e) {
      if ((e as { isRedirect?: boolean })?.isRedirect) throw e;
      throw redirect({ to: "/dashboard" });
    }
  },
  component: ShareDebugPage,
});

const CANONICAL_URLS = [
  "https://www.peopels.com/",
  "https://www.peopels.com/browse",
  "https://www.peopels.com/post-job",
  "https://www.peopels.com/become-helper",
  "https://www.peopels.com/how-it-works",
  "https://www.peopels.com/categories",
  "https://www.peopels.com/about",
] as const;

type PlatformId = "facebook" | "linkedin" | "x" | "telegram";
type Platform = {
  id: PlatformId;
  label: string;
  build: (url: string) => string;
  note: string;
};
const PLATFORMS: Platform[] = [
  {
    id: "facebook",
    label: "Facebook / Instagram",
    build: (u) =>
      `https://developers.facebook.com/tools/debug/?q=${encodeURIComponent(u)}`,
    note: "Click 'Scrape Again' on the Sharing Debugger to force a refetch.",
  },
  {
    id: "linkedin",
    label: "LinkedIn",
    build: (u) => `https://www.linkedin.com/post-inspector/inspect/${encodeURIComponent(u)}`,
    note: "LinkedIn re-scrapes automatically on inspect.",
  },
  {
    id: "x",
    label: "X (Twitter)",
    build: (u) => `https://cards-dev.twitter.com/validator?url=${encodeURIComponent(u)}`,
    note: "Login may be required; otherwise tweet the URL in draft to refresh.",
  },
  {
    id: "telegram",
    label: "Telegram",
    build: (u) => `https://t.me/WebpageBot?text=${encodeURIComponent(u)}`,
    note: "Opens Telegram with @WebpageBot — send the URL; it replies with refreshed preview.",
  },
];

type CheckRecord = {
  ts: number;
  url: string;
  platform: PlatformId;
  status: "ok" | "stale" | "broken";
  note?: string;
};

const LS_KEY = "peopels:share-debug:checks:v1";

function loadChecks(): CheckRecord[] {
  if (typeof window === "undefined") return [];
  try {
    return JSON.parse(localStorage.getItem(LS_KEY) ?? "[]") as CheckRecord[];
  } catch {
    return [];
  }
}
function saveChecks(rows: CheckRecord[]) {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(rows.slice(0, 500)));
  } catch {
    /* quota / private mode */
  }
}

function ShareDebugPage() {
  const [customUrl, setCustomUrl] = useState("");
  const [checks, setChecks] = useState<CheckRecord[]>([]);
  const [copied, setCopied] = useState<string | null>(null);

  useEffect(() => setChecks(loadChecks()), []);

  const urls = useMemo(() => {
    const c = customUrl.trim();
    return c ? [c, ...CANONICAL_URLS] : [...CANONICAL_URLS];
  }, [customUrl]);

  function record(url: string, platform: PlatformId, status: CheckRecord["status"], note?: string) {
    const next = [{ ts: Date.now(), url, platform, status, note }, ...checks];
    setChecks(next);
    saveChecks(next);
  }

  function openAll(url: string) {
    PLATFORMS.forEach((p) => window.open(p.build(url), "_blank", "noopener"));
    PLATFORMS.forEach((p) => record(url, p.id, "ok", "Opened debugger"));
  }

  function clearLog() {
    if (!confirm("Clear local debug log?")) return;
    setChecks([]);
    saveChecks([]);
  }

  function copy(text: string) {
    navigator.clipboard?.writeText(text).then(() => {
      setCopied(text);
      setTimeout(() => setCopied(null), 1200);
    });
  }

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <Header />
      <main className="mx-auto w-full max-w-6xl flex-1 px-4 py-10">
        <h1 className="font-display text-3xl font-bold">Share preview debugger</h1>
        <p className="mt-1 max-w-3xl text-sm text-muted-foreground">
          One-click open Facebook, LinkedIn, X, and Telegram preview debuggers for every canonical
          URL. Each platform requires you to be signed in to its tools; that's why we can't fully
          automate the re-scrape. Your check history is recorded locally on this device so you can
          verify caches refreshed over time.
        </p>

        <div className="mt-6 grid gap-3 sm:grid-cols-[1fr_auto]">
          <div>
            <Label htmlFor="custom">Custom URL (optional)</Label>
            <Input
              id="custom"
              type="url"
              placeholder="https://www.peopels.com/london/camden"
              value={customUrl}
              onChange={(e) => setCustomUrl(e.target.value)}
              className="mt-1"
            />
          </div>
          <div className="self-end">
            <Button variant="outline" onClick={clearLog}>
              <Trash2 className="h-4 w-4" />
              <span className="ml-2">Clear log</span>
            </Button>
          </div>
        </div>

        <section className="mt-8 space-y-3">
          {urls.map((url) => (
            <article key={url} className="rounded-2xl border border-border bg-card p-4">
              <header className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex min-w-0 items-center gap-2">
                  <span className="truncate font-mono text-xs sm:text-sm">{url}</span>
                  <button
                    type="button"
                    onClick={() => copy(url)}
                    aria-label="Copy URL"
                    className="rounded-md border border-border p-1 text-muted-foreground hover:bg-muted"
                  >
                    <Copy className="h-3.5 w-3.5" />
                  </button>
                  {copied === url && (
                    <span className="text-xs text-emerald-700 dark:text-emerald-300">copied</span>
                  )}
                </div>
                <Button size="sm" onClick={() => openAll(url)}>
                  <RotateCw className="h-4 w-4" />
                  <span className="ml-2">Open all 4 debuggers</span>
                </Button>
              </header>

              <ul className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
                {PLATFORMS.map((p) => (
                  <li
                    key={p.id}
                    className="flex flex-col gap-2 rounded-lg border border-border bg-background p-3"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-medium">{p.label}</span>
                      <a
                        href={p.build(url)}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => record(url, p.id, "ok", "Opened debugger")}
                        className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                      >
                        Open <ExternalLink className="h-3 w-3" />
                      </a>
                    </div>
                    <p className="text-[11px] leading-snug text-muted-foreground">{p.note}</p>
                    <div className="flex gap-1">
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 px-2 text-xs"
                        onClick={() => record(url, p.id, "ok", "Updated artwork confirmed")}
                      >
                        <CheckCircle2 className="h-3 w-3" />
                        <span className="ml-1">Updated</span>
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 px-2 text-xs"
                        onClick={() => record(url, p.id, "stale", "Still showing old artwork")}
                      >
                        <AlertCircle className="h-3 w-3" />
                        <span className="ml-1">Stale</span>
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
            </article>
          ))}
        </section>

        <section className="mt-10">
          <h2 className="font-display text-xl font-semibold">Check history (this device)</h2>
          <div className="mt-3 overflow-x-auto rounded-2xl border border-border bg-card">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 text-left">When</th>
                  <th className="px-3 py-2 text-left">URL</th>
                  <th className="px-3 py-2 text-left">Platform</th>
                  <th className="px-3 py-2 text-left">Status</th>
                  <th className="px-3 py-2 text-left">Note</th>
                </tr>
              </thead>
              <tbody>
                {checks.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-3 py-8 text-center text-muted-foreground">
                      No checks recorded yet.
                    </td>
                  </tr>
                )}
                {checks.map((c, i) => (
                  <tr key={i} className="border-t border-border">
                    <td className="px-3 py-2 text-xs text-muted-foreground">
                      {new Date(c.ts).toLocaleString("en-GB")}
                    </td>
                    <td className="px-3 py-2 font-mono text-xs">{c.url}</td>
                    <td className="px-3 py-2">{c.platform}</td>
                    <td className="px-3 py-2">
                      {c.status === "ok" && (
                        <Badge className="border-emerald-500/30 bg-emerald-500/15 text-emerald-700 hover:bg-emerald-500/15">
                          ok
                        </Badge>
                      )}
                      {c.status === "stale" && (
                        <Badge className="border-amber-500/30 bg-amber-500/15 text-amber-800 hover:bg-amber-500/15">
                          stale
                        </Badge>
                      )}
                      {c.status === "broken" && <Badge variant="destructive">broken</Badge>}
                    </td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">{c.note ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
